'use client';
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { LoaderCircle, Layers, X } from 'lucide-react';
import { StatusGlyph } from './StatusMark';
import dynamic from 'next/dynamic';
import { useOperationDock, type DockOperation } from '../../lib/operationDock';
import { isPluginDockRow, isPluginDockWindow } from '../../lib/operationDockPlugins';
import { useConfig } from '../../lib/queries';
import { interpolate, useTranslation } from '../../lib/i18n';
import { useEffects } from '../../lib/useEffects';
import { pluginLucideIcon } from '../../lib/pluginIcons';
import { Badge } from './Badge';
import { Button } from './Button';
import { Modal, ModalBody } from './Modal';
import { resolveToastDuration, useToast } from './Toast';

/** `loading` is what gives this import its own Suspense boundary: without it the first render of a ring
 *  suspends past the dock, and the shell's boundary (null fallback) hides the whole app, an open Account
 *  or Settings overlay included, until the chunk lands. */
const DockRing = dynamic(() => import('./MetricDonut').then(module => module.MetricDonut), {
  loading: () => <LoaderCircle size={24} aria-hidden />,
});

/** Count only time the completed control is visible and reachable. Hover, keyboard focus and an
 * isolating overlay pause the same remaining duration instead of starting another full countdown.
 * `autoDismiss` is false for a row drawn by its own view: a window the reader is looking at is closed
 * by the reader, not by a timer. */
function useDoneDismiss(
  operation: DockOperation, duration: number, paused: boolean, autoDismiss: boolean, dismiss: () => void,
) {
  const callback = useRef(dismiss);
  callback.current = dismiss;
  const remaining = useRef(duration);
  const expired = useRef(false);
  useEffect(() => {
    remaining.current = duration;
    expired.current = false;
  }, [operation.id, operation.status, duration]);
  useEffect(() => {
    if (!autoDismiss || operation.status !== 'succeeded' || paused || expired.current) return;
    const started = Date.now();
    const timer = setTimeout(() => {
      expired.current = true;
      callback.current();
    }, remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (Date.now() - started));
    };
  }, [operation.id, operation.status, duration, paused, autoDismiss]);
}

function useIsolated(ref: RefObject<HTMLElement | null>): boolean {
  const [isolated, setIsolated] = useState(false);
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    const update = () => setIsolated(Boolean(ref.current?.closest('[inert]')));
    update();
    const visibility = () => setHidden(document.hidden);
    visibility();
    document.addEventListener('visibilitychange', visibility);
    const observer = new MutationObserver(update);
    observer.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['inert'] });
    return () => { observer.disconnect(); document.removeEventListener('visibilitychange', visibility); };
  }, [ref]);
  return isolated || hidden;
}

function OperationIcon({ operation }: { operation: DockOperation }) {
  const { motionEnabled } = useEffects();
  const { t } = useTranslation();
  if (operation.status === 'succeeded') return <span data-testid="operation-dock-done"><StatusGlyph kind="success" tone="success" size={26} /></span>;
  if (operation.status === 'failed') return <StatusGlyph kind="error" tone="danger" size={26} />;
  // A source may name its own glyph for a row that is an identity rather than a quantity (an open
  // terminal). It replaces the PROGRESS glyph only: a settled row still reports its outcome with the
  // shared check or alert, and the caption beside it keeps saying which row this is.
  if (isPluginDockRow(operation) && operation.icon) {
    const Icon = pluginLucideIcon(operation.icon);
    return <Icon size={20} aria-hidden />;
  }
  if (operation.percent === null) return <LoaderCircle size={24} className={motionEnabled ? 'animate-spin' : undefined} aria-hidden />;
  return <span aria-hidden>
    <DockRing percent={operation.percent} colour="var(--color-primary)" label={t.operationProgress.progressLabel}
      valueText={`${Math.round(operation.percent)} %`} centre={<Layers size={20} aria-hidden />} />
  </span>;
}

function DockButton({ operation, label, caption, duration, isolated, open, dismiss }: {
  operation: DockOperation; label: string; caption?: string; duration: number; isolated: boolean;
  open: () => void; dismiss: () => void;
}) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  useDoneDismiss(operation, duration, isolated || hovered || focused, !isPluginDockWindow(operation), dismiss);
  const settled = operation.status === 'succeeded' || operation.status === 'failed';
  return <div className="flex shrink-0 flex-col items-end gap-1" onPointerEnter={() => setHovered(true)} onPointerLeave={() => setHovered(false)}
    onFocusCapture={() => setFocused(true)} onBlurCapture={event => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
    }}>
    {/* The row's own box, so the dismissal control stays centred on the ROUND control and not on the
        column the caption adds below it. */}
    <div className={`relative ${settled ? 'pl-10' : ''}`}>
      <Button variant="default" size="icon" className="operation-dock-button relative bg-card text-foreground shadow-raised"
        data-testid="operation-dock-button" data-operation-id={operation.id} data-state={operation.status}
        aria-label={label} title={label} onClick={open}>
        <OperationIcon operation={operation} />
      </Button>
      {settled ? <Button variant="ghost" size="icon" className="absolute left-0 top-1/2 size-8 -translate-y-1/2 rounded-full border-border bg-card shadow-raised"
        aria-label={`${t.operationDock.dismiss}: ${label}`} title={t.operationDock.dismiss} onClick={dismiss}>
        <X size={12} aria-hidden />
      </Button> : null}
    </div>
    {/* Decorative: the control's accessible name already carries the caption, and the caption's own text
        is trimmed, so it says which row this is to the eye that needs it and to nobody twice. */}
    {caption ? <Badge aria-hidden data-operation-caption tone="muted" className="max-w-40 truncate">{caption}</Badge> : null}
  </div>;
}

/** One shell host for every background operation, with no portal or persisted browser copy. */
export function OperationDock() {
  const dock = useOperationDock();
  const { t } = useTranslation();
  const { toast } = useToast();
  const config = useConfig();
  const region = useRef<HTMLDivElement>(null);
  const isolated = useIsolated(region);
  const duration = resolveToastDuration(config.data?.runtime?.limits);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const pendingOpen = useRef<string | null>(null);
  const selected = dock.items.find(item => item.id === dock.openId);
  // A source may caption a row (three open terminals are three identical round glyphs). The caption is
  // what tells them apart, so the control's accessible name carries it too.
  const captionOf = (operation: DockOperation) => isPluginDockRow(operation) ? operation.caption : undefined;
  const accessibleLabel = (operation: DockOperation) => {
    const action = operation.title;
    const label = operation.statusLabel !== undefined ? `${action} · ${operation.statusLabel}`
      : operation.status === 'succeeded' ? interpolate(t.operationDock.done, { action: action })
        : operation.status === 'failed' ? interpolate(t.operationDock.failed, { action: action })
          : `${t.operationProgress.reopen}: ${action} · ${operation.percent === null ? t.operationProgress.indeterminate : `${Math.round(operation.percent)} %`}`;
    const caption = captionOf(operation);
    return caption === undefined ? label : interpolate(t.operationDock.captioned, { action: label, caption });
  };
  const [announcement, setAnnouncement] = useState('');
  const announced = useRef(new Map<string, string>());
  useEffect(() => {
    const fresh = dock.items.filter(item => {
      const previous = announced.current.get(item.id);
      return previous === undefined || ((item.status === 'succeeded' || item.status === 'failed') && previous !== item.status);
    });
    for (const item of dock.items) announced.current.set(item.id, item.status);
    if (fresh.length) setAnnouncement(fresh.map(item => {
      const message = item.status === 'succeeded' ? t.operationDock.done
        : item.status === 'failed' ? t.operationDock.failed : t.operationDock.started;
      return item.statusLabel !== undefined ? `${item.title} · ${item.statusLabel}` : interpolate(message, { action: item.title });
    }).join('. '));
  }, [dock.items, t.operationDock.started, t.operationDock.done, t.operationDock.failed]);

  const { acknowledge } = dock;
  const dismiss = useCallback((id: string) => {
    void acknowledge(id).catch(error => toast(t.operationDock.ackFailed, 'error', { error }));
  }, [acknowledge, toast, t.operationDock.ackFailed]);
  // Modal's onClosed runs after its own focus restoration. Keep the closing id until that hand-off.
  const closingId = useRef<string | null>(null);
  const returnToDock = () => {
    const id = closingId.current;
    const target = id ? region.current?.querySelector<HTMLElement>(`[data-operation-id="${CSS.escape(id)}"]`) : null;
    // Closing the final failure can acknowledge and remove its only dock button before focus returns.
    const next = target ?? region.current?.querySelector<HTMLElement>('button') ?? document.querySelector<HTMLElement>('.advisor-launcher');
    if (next && !next.closest('[inert]')) next.focus({ preventScroll: true });
    closingId.current = null;
  };
  const closeProgress = () => {
    closingId.current = dock.openId;
    // Dismissing a failed PROGRESS DIALOG acknowledges its result: a finished attempt the reader has just
    // read has nothing left to inspect. A row drawn by its own view is not that: closing a window only
    // minimizes it (the view keeps its own way to end the work), so its result outlives the closing.
    if (selected?.status === 'failed' && !isPluginDockWindow(selected)) dismiss(dock.openId!);
    dock.minimize();
  };
  // The inspected item keeps its button: the open dialog makes the dock inert, which hides the controls
  // but keeps the column's footprint, so the page beside it does not reflow while the dialog is open.
  // The column grows upward from the advisor launcher (from the corner itself on a phone, which has no
  // launcher): the first item sits nearest to it and `+N` farthest, so the DOM runs top to bottom in the
  // same order the eye and the Tab key do.
  const first = dock.items.slice(0, 3).reverse();
  const rest = dock.items.slice(3);
  const overflowFailures = rest.filter(item => item.status === 'failed');

  return <>
    <div ref={region} role="region" aria-label={t.operationDock.region} data-testid="operation-dock"
      className="operation-dock overlay-layer-fab">
      {rest.length ? <Button variant="default" size="icon"
        className={`operation-dock-button bg-card shadow-raised ${overflowFailures.length ? 'text-destructive' : ''}`}
        data-testid="operation-dock-more"
        aria-label={[`${t.operationDock.region}: ${interpolate(t.operationDock.more, { count: String(rest.length) })}`,
          ...overflowFailures.map(accessibleLabel)].join('. ')}
        onClick={() => setOverflowOpen(true)}>
        {overflowFailures.length ? <StatusGlyph kind="error" tone="danger" size={16} /> : null}
        {interpolate(t.operationDock.more, { count: String(rest.length) })}
      </Button> : null}
      {first.map(item => <DockButton key={item.id} operation={item} label={accessibleLabel(item)} caption={captionOf(item)}
        duration={duration} isolated={isolated || overflowOpen} open={() => dock.showProgress(item.id)} dismiss={() => dismiss(item.id)} />)}
    </div>
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true" data-testid="operation-dock-announcement">{announcement}</div>
    {overflowOpen ? <Modal title={t.operationDock.region} size="sm" presentation="center" onClose={() => setOverflowOpen(false)}
      onClosed={() => { const id = pendingOpen.current; pendingOpen.current = null; if (id) dock.showProgress(id); }}>
      <ModalBody>
        {rest.map(operation => <div key={operation.id} className="flex min-w-0 items-center gap-2">
          <Button variant="ghost" className="min-w-0 flex-1 justify-start whitespace-normal text-left"
            onClick={() => { pendingOpen.current = operation.id; setOverflowOpen(false); }}>
            <span className="min-w-0 break-words">{accessibleLabel(operation)}</span>
          </Button>
          {operation.status === 'succeeded' || operation.status === 'failed'
            ? <Button variant="ghost" size="icon" aria-label={`${t.operationDock.dismiss}: ${operation.title}`} onClick={() => dismiss(operation.id)}><X size={16} aria-hidden /></Button> : null}
        </div>)}
      </ModalBody>
    </Modal> : null}
    {selected ? selected.renderProgress({
      onClose: closeProgress, onClosed: returnToDock, successDelayMs: duration,
      onAccepted: dock.open, acknowledge: dock.acknowledge,
    }) : null}
  </>;
}
